import { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw, Trash2, Upload } from 'lucide-react';
import { ACCEPTED_IMAGE_MIME_LIST, validateImageFile, useTranslation } from '@dukanti/shared';
import type { StoreDetailsErrorKey, TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';

export interface StoreLogoFieldProps {
  readonly currentLogoUrl: string | null;
  readonly selectedFile: File | null;
  readonly removeRequested: boolean;
  readonly errorKey: StoreDetailsErrorKey | null;
  readonly onSelectedFileChange: (file: File | null) => void;
  readonly onRemoveRequestedChange: (removeRequested: boolean) => void;
  readonly onErrorChange: (errorKey: StoreDetailsErrorKey | null) => void;
}

export function StoreLogoField({
  currentLogoUrl,
  selectedFile,
  removeRequested,
  errorKey,
  onSelectedFileChange,
  onRemoveRequestedChange,
  onErrorChange,
}: StoreLogoFieldProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setSelectedPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const visiblePreviewUrl = removeRequested ? null : (selectedPreviewUrl ?? currentLogoUrl);
  const hasExistingLogo = currentLogoUrl !== null;

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      onErrorChange(null);
      if (!file) return;
      const validationError = validateImageFile(file);
      if (validationError === 'imageTooLarge') {
        onSelectedFileChange(null);
        onRemoveRequestedChange(false);
        onErrorChange('admin.storeSettings.errors.logoTooLarge');
        event.target.value = '';
        return;
      }
      if (validationError === 'imageInvalidType') {
        onSelectedFileChange(null);
        onRemoveRequestedChange(false);
        onErrorChange('admin.storeSettings.errors.logoInvalidType');
        event.target.value = '';
        return;
      }
      onSelectedFileChange(file);
      onRemoveRequestedChange(false);
    },
    [onErrorChange, onRemoveRequestedChange, onSelectedFileChange],
  );

  return (
    <div data-testid="store-details-logo-field">
      <div className="flex flex-wrap items-start gap-4">
        <div className="border-border bg-muted flex size-28 shrink-0 items-center justify-center overflow-hidden rounded-xl border">
          {visiblePreviewUrl ? (
            <img
              src={visiblePreviewUrl}
              alt={t('admin.storeSettings.details.logoLabel')}
              className="size-full object-cover"
              data-testid="store-details-logo-preview"
            />
          ) : (
            <Upload className="text-muted-foreground size-8" aria-hidden />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground text-start text-sm">
            {t('admin.storeSettings.details.logoHint')}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              data-testid="store-details-logo-upload"
            >
              <Upload className="me-2 size-4" aria-hidden />
              {t(
                hasExistingLogo || selectedFile
                  ? 'admin.storeSettings.details.logoReplace'
                  : 'admin.storeSettings.details.logoUpload',
              )}
            </Button>
            {hasExistingLogo || selectedFile || removeRequested ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (removeRequested) {
                    onRemoveRequestedChange(false);
                  } else {
                    onSelectedFileChange(null);
                    onRemoveRequestedChange(true);
                  }
                  onErrorChange(null);
                }}
                data-testid={
                  removeRequested ? 'store-details-logo-undo-remove' : 'store-details-logo-remove'
                }
              >
                {removeRequested ? (
                  <RotateCcw className="me-2 size-4" aria-hidden />
                ) : (
                  <Trash2 className="me-2 size-4" aria-hidden />
                )}
                {t(
                  removeRequested
                    ? 'admin.storeSettings.details.logoUndoRemove'
                    : 'admin.storeSettings.details.logoRemove',
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_MIME_LIST}
        className="sr-only"
        onChange={handleFileChange}
        data-testid="store-details-logo-input"
        aria-label={t('admin.storeSettings.details.logoLabel')}
      />

      {errorKey ? (
        <p
          role="alert"
          className="text-destructive mt-2 text-start text-sm"
          data-testid="store-details-logo-error"
        >
          {t(errorKey as TranslationKey)}
        </p>
      ) : null}
    </div>
  );
}
